import Login from "@/pages/Auth/Login";
import Register from "@/pages/Auth/Register";
import ForgotPassword from "@/pages/Auth/ForgotPassword";
import EmailVerification from "@/pages/Auth/EmailVerification";
import Patients from "@/pages/Patients.tsx";
import NotFound from "@/pages/NotFound.tsx";
import Dashboard from "@/pages/Dashboard.tsx";
import Create from "@/pages/patient/Create.tsx";
import SearchPatient from "@/pages/SearchPatient.tsx";
import HIPPAConsent from "@/pages/patient/HIPPAConsent.tsx";
import NewPatientRedirect from "@/pages/patient/NewPatientRedirect.tsx";
import Questionnaire from "@/pages/patient/Questionnaire.tsx";
import QuestionnaireInfo from "@/pages/QuestionnaireInfo.tsx";
import TokenQuestionnaire from "@/pages/patient/TokenQuestionnaire.tsx";
import Profile from "@/pages/account/Profile.tsx";
import EdgeFunctions from "@/pages/EdgeFunctions.tsx";
import GuestQuestionnaire from "@/pages/guest/GuestQuestionnaire.tsx";
import GuestHIPPAConsent from "@/pages/guest/GuestHIPPAConsent.tsx";
import QuestionnaireResponse from "@/pages/questionnaire/QuestionnaireResponses.tsx";
import QuestionnaireAccess from "@/pages/QuestionnaireAccess.tsx";
import QuestionnaireWithAccess from "@/pages/patient/QuestionnaireWithAccess.tsx";
import PatientDetails from "@/pages/patient/PatientDetails.tsx";
import UpdatePassword from "@/pages/Auth/UpdatePassword.tsx";
import AuthCallback from "@/pages/Auth/AuthCallback.tsx";
import CallConfirmation from "@/pages/patient/CallConfirmation.tsx";

// Import new public components
import PublicQuestionnaireInfo from "@/pages/questionnaire/PublicQuestionnaireInfo";
import PublicQuestionnaire from "@/pages/questionnaire/PublicQuestionnaire.tsx";
import PublicPatientLogReport from "@/pages/patient/PublicPatientLogReport";

// Import the route protection components
import ProtectedRoute from "@/routes/ProtectedRoute";
import PublicRoute from "@/routes/PublicRoute";
import PatientQuestionnaireWrapper from "@/pages/SMSFlow/PatientQuestionnaireWrapper";
import AudioArchives from "@/pages/admin/audio-archives";
import Appointments from "@/pages/appointments/Appointments";
import NotificationsScreen from "@/pages/NotificationsScreen";
import StaffList from "@/pages/admin/StaffList";
import StaffDetails from "@/pages/admin/StaffDetails";
import HospitalManagement from "@/pages/admin/HospitalManagement";
import CmsAlerts from "@/pages/cms-alerts/CmsAlerts";
import LeadPatientRegistration from "@/pages/patient/LeadPatientRegistration";
import AppointmentConfirmation from "@/pages/AppointmentConfirmation";

const AppRoutes = [
  {
    path: "/rds/calls/call-confirm",
    element: <CallConfirmation />,
  },
  // Root route - redirect to dashboard if authenticated, login if not
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },

  // Protected routes - require authentication
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: "/profile",
    element: (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    ),
  },
  {
    path: "/edge-functions",
    element: (
      <ProtectedRoute>
        <EdgeFunctions />
      </ProtectedRoute>
    ),
  },
  {
    path: "/patients/:patientId/:encounterId/create",
    element: (
      <ProtectedRoute>
        <Create />
      </ProtectedRoute>
    ),
  },
  {
    path: "patients/hippa-consent",
    element: (
      <ProtectedRoute>
        <NewPatientRedirect />
      </ProtectedRoute>
    ),
  },
  {
    path: "patients/:patientId/hippa-consent",
    element: (
      <PublicRoute>
        <HIPPAConsent />
      </PublicRoute>
    ),
  },
  {
    path: "/patient-enrollment-report",
    element: (
      <PublicRoute>
        <PublicPatientLogReport />
      </PublicRoute>
    ),
  },
  {
    path: "/public/patient-enrollment-report",
    element: (
      <PublicRoute>
        <PublicPatientLogReport />
      </PublicRoute>
    ),
  },
  {
    path: "/patient-log-report",
    element: (
      <PublicRoute>
        <PublicPatientLogReport />
      </PublicRoute>
    ),
  },
  {
    path: "/public/patient-log-report",
    element: (
      <PublicRoute>
        <PublicPatientLogReport />
      </PublicRoute>
    ),
  },
  {
    path: "patients/:patientId/:encounterId/questionnaire-info",
    element: (
      <ProtectedRoute>
        <QuestionnaireInfo />
      </ProtectedRoute>
    ),
  },
  {
    path: "patients/:patientId/:encounterId/questionnaire/:type",
    element: (
      <ProtectedRoute>
        <Questionnaire />
      </ProtectedRoute>
    ),
  },
  {
    path: "/patients/search",
    element: (
      <ProtectedRoute>
        <SearchPatient />
      </ProtectedRoute>
    ),
  },
  {
    path: "/patients/list",
    element: (
      <ProtectedRoute>
        <Patients />
      </ProtectedRoute>
    ),
  },
  {
    path: "/patients/create",
    element: (
      <ProtectedRoute>
        <Create />
      </ProtectedRoute>
    ),
  },
  {
    path: "/patients/:id",
    element: (
      <ProtectedRoute>
        <PatientDetails />
      </ProtectedRoute>
    ),
  },
  {
    path: "/questionnaire-responses",
    element: (
      <ProtectedRoute>
        <QuestionnaireResponse />
      </ProtectedRoute>
    ),
  },
  {
    path: "/questionnaire/:id",
    element: (
      <ProtectedRoute>
        <QuestionnaireWithAccess />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/audio-archives",
    element: (
      <ProtectedRoute requiredRoles={["Admin", "BranchAdmin", "Branch Admin", "Doctor", "Nurse", "Staff", "Technician", "clinical_staff", "Clinical_reviewer", "CareCoordinator"]}>
        <AudioArchives />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/staff",
    element: (
      <ProtectedRoute requiredRoles={["Admin", "BranchAdmin"]}>
        <StaffList />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/staff/:id",
    element: (
      <ProtectedRoute requiredRoles={["Admin", "BranchAdmin"]}>
        <StaffDetails />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/hospitals",
    element: (
      <ProtectedRoute requiredRoles={["Admin", "BranchAdmin", "Branch Admin"]}>
        <HospitalManagement />
      </ProtectedRoute>
    ),
  },
  {
    path: "/appointments",
    element: (
      <ProtectedRoute>
        <Appointments />
      </ProtectedRoute>
    ),
  },
  {
    path: "/cms-alerts",
    element: (
      <ProtectedRoute>
        <CmsAlerts />
      </ProtectedRoute>
    ),
  },
  {
    path: "/notifications",
    element: (
      <ProtectedRoute>
        <NotificationsScreen />
      </ProtectedRoute>
    ),
  },

  // Public routes - redirect to dashboard if already authenticated
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/auth/register",
    element: (
      <PublicRoute>
        <Register />
      </PublicRoute>
    ),
  },
  {
    path: "/verify-email",
    element: (
      <PublicRoute>
        <EmailVerification />
      </PublicRoute>
    ),
  },
  {
    path: "/auth/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallback />,
  },
  {
    path: "/update-password",
    element: (
      <ProtectedRoute>
        <UpdatePassword />
      </ProtectedRoute>
    ),
  },

  // Guest/public routes - no authentication required
  {
    path: "questionnaire/t/:token",
    element: <TokenQuestionnaire />,
  },
  {
    path: "visitor/id/:patientId/hippa-consent",
    element: <GuestHIPPAConsent />,
  },
  {
    path: "visitor/id/:patientId/questionnaire/:type",
    element: <GuestQuestionnaire />,
  },

  // NEW: Public questionnaire routes for SMS access (no authentication required)
  {
    path: "/public/patients/:patientId/questionnaire-info",
    element: <PublicQuestionnaireInfo />,
  },
  {
    path: "/public/patients/:patientId/questionnaire/:type",
    element: <PublicQuestionnaire />,
  },

  // NEW: SMS Questionnaire Access Route - Clean and Simple
  {
    path: "/q/:code",
    element: <QuestionnaireAccess />,
  },
  // NEW: Call Confirmation Route
  {
    path: "/c/:code",
    element: <CallConfirmation />,
  },
  {
    path: "/patient-questionnaire-wrapper/:patientId",
    element: <PatientQuestionnaireWrapper />,
  },
  // Lead Self-Registration (10-minute validity, single submit)
  {
    path: "/register/lead/:token",
    element: (
      <PublicRoute>
        <LeadPatientRegistration />
      </PublicRoute>
    ),
  },
  {
    path: "/public/register/:token",
    element: (
      <PublicRoute>
        <LeadPatientRegistration />
      </PublicRoute>
    ),
  },
  // Public appointment confirmation routes
  {
    path: "/confirm",
    element: <AppointmentConfirmation />,
  },
  {
    path: "/confirm/:token",
    element: <AppointmentConfirmation />,
  },

  // 404 route
  {
    path: "*",
    element: <NotFound />,
  },
];

export default AppRoutes;
